import { Ref, ref } from 'vue';
import { PaginationRequestModel, SubtaskModel } from '../../../models';
import { AdapterService } from '../adapter';
import {
  AttachSubtaskModel,
  GetSubtasksModel,
  SearchTasksModel,
  TaskModel,
  TaskParentModel,
} from '../models';
import {
  SubtasksSearchFunctionType,
  SubtasksSearchResponseModel,
} from '../../task-subtasks-search-dropdown';
import { TirPmNotificationEventBus, TirPmNotificationModel } from 'tir-components';

type UseTaskSubtasks = {
  /**Текущая задача */
  currentTask: Ref<TaskModel | undefined>;
  /**При успешной установке родительской задачи */
  onSuccessSetParentTask?: (parentTaskId: number) => Promise<any> | any;
  onUnAttachParentTask?: (subtaskId: number) => Promise<any> | any;
};

export const useTaskSubtasks = (params: UseTaskSubtasks) => {
  const adapter = new AdapterService();
  /**Были ли изменены подазадчи */
  const isChangedSubtasks = ref(false);
  /**Загрузка при откреплении родительской задачи */
  const isLoadingUnattachParent = ref(false);
  /**Подзадачи которые в загрузке на открепление */
  const isLoadingUnattachSubtask = ref(new Set<number>());

  /**Список подзадач */
  const subtasks = ref(new Array<SubtaskModel>());
  /**Кол-во подзадач */
  const countSubtasks = ref(0);
  /**Игнорируемы подзадачи при поиске */
  const ignoreSearchSubtasks = ref(new Array<number>());

  /**Получить список подзадач */
  const getSubtasks = async () => {
    if (!params.currentTask.value?.Id) return;

    const resp = await adapter.getSubtasks(
      new GetSubtasksModel({
        TaskId: params.currentTask.value.Id,
        QueueTaskId: params.currentTask.value.QueueTaskId,
      }),
    );
    if (resp.IsSuccess) {
      countSubtasks.value = resp.Count;
      subtasks.value = resp.Items;
    }
  };

  /**Показать все подзадачи */
  const showAllSubtasks = async () => {
    if (!params.currentTask.value?.Id) return;
    await getSubtasks();
  };

  /**
   * Прикрепить подзадачу
   * @param subtaskId ID подазадчи
   */
  const attachSubtask = async (subtaskId: number) => {
    if (!params.currentTask.value?.Id) return;

    const request = new AttachSubtaskModel({
      TaskId: subtaskId,
      ParentTaskId: params.currentTask.value.Id,
    });

    const resp = await adapter.attachSubtask(request);
    if (resp.IsSuccess) {
      isChangedSubtasks.value = true;
      ignoreSearchSubtasks.value.push(subtaskId);
      await getSubtasks();

      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Подзадача была успешно добавлена',
          type: 'success',
          group: 'general',
          life: 2000,
          closable: true,
        }),
      );
    } else {
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Произошла ошибка при добавлении подзадачи',
          type: 'error',
          group: 'general',
          life: 2000,
          closable: true,
        }),
      );
    }
  };

  /**
   * Убрать подазадачу
   * @param subtaskId ID подзадачи
   */
  const removeSubtask = async (subtaskId: number) => {
    isLoadingUnattachSubtask.value.add(subtaskId);
    const resp = await adapter.removeSubtask(subtaskId);
    isLoadingUnattachSubtask.value.delete(subtaskId);
    if (resp.IsSuccess) {
      ignoreSearchSubtasks.value = ignoreSearchSubtasks.value.filter(
        (_subtaskId) => subtaskId !== subtaskId,
      );
      isChangedSubtasks.value = true;
      subtasks.value = subtasks.value.filter((subtask) => subtask.Id !== subtaskId);
      countSubtasks.value--;
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Подзадача была успешно удалена',
          type: 'success',
          group: 'general',
          life: 2000,
          closable: true,
        }),
      );
    } else {
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Произошла ошибка при удалении подзадачи',
          type: 'error',
          group: 'general',
          life: 2000,
          closable: true,
        }),
      );
    }
  };

  /**Установить родительскую задачу */
  const setParentTask = async (parentTaskId: number, isChange: boolean) => {
    if (!params.currentTask.value?.Id) return;
    const resp = await adapter.attachSubtask(
      new AttachSubtaskModel({
        ParentTaskId: parentTaskId,
        TaskId: params.currentTask.value.Id,
      }),
    );

    if (resp.IsSuccess) {
      await getSubtasks();
      await params.onSuccessSetParentTask?.(parentTaskId);
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: isChange
            ? 'Успешная смена родительской задачи'
            : 'Родительская задача была прикреплена',
          type: 'success',
          group: 'general',
          life: 2000,
          closable: true,
        }),
      );
    } else {
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: resp.ErrorMessage,
          type: 'error',
          group: 'general',
          life: 2000,
          closable: true,
        }),
      );
    }
  };

  /**Открепить родительскую задачу */
  const unAttachParentTask = async () => {
    if (!params.currentTask.value?.Id || !params.currentTask.value.Parent) return;
    isLoadingUnattachParent.value = true;
    const resp = await adapter.removeSubtask(params.currentTask.value.Id);
    isLoadingUnattachParent.value = false;
    if (resp.IsSuccess) {
      await params.onUnAttachParentTask?.(params.currentTask.value.Id);
      if (params.currentTask?.value?.Parent)
        Object.assign(params.currentTask?.value?.Parent, new TaskParentModel());
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Родительская задача была откреплена',
          type: 'success',
          group: 'general',
          life: 2000,
          closable: true,
        }),
      );
    }
  };

  /**Поиск задач для добавления */
  const searchTasksToAttach: SubtasksSearchFunctionType = async (request) => {
    const resp = await adapter.searchTasks(
      new SearchTasksModel({
        Page: new PaginationRequestModel({
          Skip: request.Skip,
          Take: request.Take,
        }),
        SearchValue: request.SearchValue,
        NamespaceId: params.currentTask.value.NamespaceId,
        WithoutChildTask: true,
        IgnoreTaskList: [params.currentTask.value?.Id].filter(Boolean),
      }),
    );

    return new SubtasksSearchResponseModel({
      IsSuccess: resp.IsSuccess,
      Count: resp.Count,
      Size: resp.Size,
      Items: resp.Items,
    });
  };

  /**Поиск задач для смены родителя */
  const searchTasksChangeParent: SubtasksSearchFunctionType = async (request) => {
    const resp = await adapter.searchTasks(
      new SearchTasksModel({
        Page: new PaginationRequestModel({
          Skip: request.Skip,
          Take: request.Take,
        }),
        SearchValue: request.SearchValue,
        QueueTaskId: request.QueueId,
        WithoutChildTask: false,
        IgnoreTaskList: [params.currentTask.value?.Id, params.currentTask.value?.Parent?.Id].filter(
          Boolean,
        ),
      }),
    );

    return new SubtasksSearchResponseModel({
      IsSuccess: resp.IsSuccess,
      Count: resp.Count,
      Size: resp.Size,
      Items: resp.Items,
    });
  };

  /**Сброс данных */
  const resetSubtasksData = () => {
    isChangedSubtasks.value = false;
    subtasks.value = new Array<SubtaskModel>();
    countSubtasks.value = 0;
    ignoreSearchSubtasks.value = [];
  };

  return {
    /**Были ли изменены подазадчи */
    isChangedSubtasks,
    /**Загрузка при откреплении родительской задачи */
    isLoadingUnattachParent,
    /**Подзадачи которые в загрузке на открепление */
    isLoadingUnattachSubtask,
    /**Список подзадач */
    subtasks,
    /**Кол-во подзадач */
    countSubtasks,
    /**Игнорируемы подзадачи при поиске */
    ignoreSearchSubtasks,
    /**Получить список подзадач */
    getSubtasks,
    /**Показать все подзадачи */
    showAllSubtasks,
    /**
     * Прикрепить подзадачу
     * @param subtaskId ID подазадчи
     */
    attachSubtask,
    /**
     * Убрать подазадачу
     * @param subtaskId ID подзадачи
     */
    removeSubtask,
    /**Установить родительскую задачу */
    setParentTask,
    /**Открепить родительскую задачу */
    unAttachParentTask,
    /**Поиск задач для добавления */
    searchTasksToAttach,
    /**Поиск задач для смены родителя */
    searchTasksChangeParent,
    /**Сброс данных */
    resetSubtasksData,
  };
};
